import { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { 
  Box, 
  Stack, 
  Skeleton, 
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleIcon from '@mui/icons-material/People';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import PercentIcon from '@mui/icons-material/Percent';
import DownloadIcon from '@mui/icons-material/Download';

import { useApi } from '../../hooks/useApi';
import { StatsCard } from './StatsCard';
import { ParticipationTable } from './ParticipationTable';
import { QuizLeaderboard } from './QuizLeaderboard';
import { RecentActivity } from './RecentActivity';

interface ReportsDashboardProps {
  agentId: number;
}

export const ReportsDashboard = ({ agentId }: ReportsDashboardProps) => {
  const { user } = useUser();
  const { apiFetch } = useApi();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [participationData, setParticipationData] = useState<any>(null);
  const [dateRange, setDateRange] = useState('all');

  const fetchData = async () => {
    if (!user?.id || !agentId) return;

    setLoading(true);
    setError(null);

    try {
      // Build date params
      let dateParams = '';
      if (dateRange !== 'all') {
        const now = new Date();
        let from = new Date();
        
        switch (dateRange) {
          case '7d':
            from.setDate(now.getDate() - 7);
            break;
          case '30d':
            from.setDate(now.getDate() - 30);
            break;
          case '90d':
            from.setDate(now.getDate() - 90);
            break;
        }
        dateParams = `?from=${from.toISOString().split('T')[0]}`;
      }

      // Fetch dashboard summary
      const dashRes = await apiFetch(`agents/${agentId}/reports/dashboard${dateParams}`);
      if (!dashRes.ok) throw new Error('Failed to fetch dashboard data');
      const dashData = await dashRes.json();
      setDashboardData(dashData);

      // Fetch participation data
      const partRes = await apiFetch(`agents/${agentId}/reports/participation${dateParams}`);
      if (!partRes.ok) throw new Error('Failed to fetch participation data');
      const partData = await partRes.json();
      setParticipationData(partData);

    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.id, agentId, dateRange]);

  const handleExport = async (type: 'participation' | 'quizzes') => {
    try {
      let dateParams = '';
      if (dateRange !== 'all') {
        const now = new Date();
        let from = new Date();
        switch (dateRange) {
          case '7d': from.setDate(now.getDate() - 7); break;
          case '30d': from.setDate(now.getDate() - 30); break;
          case '90d': from.setDate(now.getDate() - 90); break;
        }
        dateParams = `&from=${from.toISOString().split('T')[0]}`;
      }

      const res = await apiFetch(`agents/${agentId}/reports/export?report_type=${type}&format=csv${dateParams}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  if (loading) {
    return (
      <Stack spacing={3}>
        <Stack direction="row" spacing={2}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} variant="rounded" width="20%" height={140} />
          ))}
        </Stack>
        <Skeleton variant="rounded" width="100%" height={400} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: '12px' }}>
        {error}
      </Alert>
    );
  }

  const overview = dashboardData?.overview || {};
  const topPerformers = dashboardData?.top_performers || [];
  const recentActivity = dashboardData?.recent_activity || [];
  const members = participationData?.members || [];

  return (
    <Stack spacing={3}>
      {/* Controls */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={dateRange}
            label="Time Period"
            onChange={(e) => setDateRange(e.target.value)}
            sx={{ borderRadius: '8px' }}
          >
            <MenuItem value="all">All Time</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => handleExport('participation')}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Export CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        <StatsCard
          title="Team Members"
          value={overview.total_team_members || 0}
          subtitle={`${overview.unique_participants || 0} active`}
          icon={<PeopleIcon sx={{ color: '#0099CC' }} />}
          color="#0099CC"
        />
        <StatsCard
          title="Quizzes Sent"
          value={overview.total_quizzes || 0}
          subtitle={`${overview.pending || 0} pending`}
          icon={<QuizIcon sx={{ color: '#8B5CF6' }} />}
          color="#8B5CF6"
        />
        <StatsCard
          title="Completed"
          value={overview.completed || 0}
          subtitle="quizzes graded"
          icon={<CheckCircleIcon sx={{ color: '#10B981' }} />}
          color="#10B981"
        />
        <StatsCard
          title="Avg Score"
          value={`${overview.avg_score || 0}%`}
          subtitle="across all quizzes"
          icon={<PercentIcon sx={{ color: '#F59E0B' }} />}
          color="#F59E0B"
        />
        <StatsCard
          title="Total XP"
          value={overview.total_xp || 0}
          subtitle={`${overview.participation_rate || 0}% participation`}
          icon={<StarIcon sx={{ color: '#EF4444' }} />}
          color="#EF4444"
        />
      </Stack>

      {/* Leaderboard & Recent Activity */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <QuizLeaderboard performers={topPerformers} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <RecentActivity activities={recentActivity} />
        </Box>
      </Stack>

      {/* Participation Table */}
      <ParticipationTable members={members} />
    </Stack>
  );
};
